import moment from "moment-timezone"
import { DocumentSnapshot, GeoPoint, logger } from "../firestoreImports"
import { z } from "zod"

export enum ReservationType {
    Day,
    Halfday,
    Hours,
    Minutes
}

const locationSchema = z
    .object({
        title: z.string().nullish(),
        hoursPreceding: z.number().nullish(),
        timezone: z.string().default("Europe/Prague"),
        reservationType: z.enum(ReservationType).default(ReservationType.Day),
        hoursPrecedingPattern: z.array(z.number()).length(7).nullish(),
        location: z.custom<GeoPoint>().nullish(),
        coordinates: z.custom<GeoPoint>().nullish(),
        overflowLocationIds: z.array(z.string()).nullish()
    })
    .transform(input => {
        const hoursPreceding = input.hoursPreceding ?? 4
        const hoursPrecedingPattern = input.hoursPrecedingPattern ?? Array(7).fill(hoursPreceding)
        const lat = input.coordinates instanceof GeoPoint ? input.coordinates.latitude : undefined
        const lng = input.coordinates instanceof GeoPoint ? input.coordinates.longitude : undefined
        return {
            ...input,
            hoursPreceding,
            hoursPrecedingPattern,
            lat: lat ?? input.location?.latitude,
            lng: lng ?? input.location?.longitude
        }
    })

interface ILocation {
    id: string
    title: string
    hoursPreceding: number
    timezone: string
    reservationType: ReservationType
    hoursPrecedingPattern: number[]
    lat?: number
    lng?: number
    overflowLocationIds?: string[]
}

export interface Location extends ILocation {}
export class Location {
    constructor(params: ILocation) {
        Object.assign(this, params)
    }

    static fromData(doc: DocumentSnapshot): Location | null {
        const raw = doc.data()
        if (!raw) {
            logger.debug(`Cannot create Location ${doc.id} from snapshot: missing data`)
            return null
        }

        try {
            const parsed = locationSchema.parse(raw)
            return new Location({
                id: doc.id,
                title: parsed.title ?? doc.id,
                hoursPreceding: parsed.hoursPreceding,
                timezone: parsed.timezone,
                reservationType: parsed.reservationType,
                hoursPrecedingPattern: parsed.hoursPrecedingPattern,
                lat: parsed.location?.latitude ?? undefined,
                lng: parsed.location?.longitude ?? undefined,
                overflowLocationIds: parsed.overflowLocationIds ?? undefined
            })
        } catch (error) {
            logger.error(`Cannot create Location ${doc.id} from snapshot:`, error)
            return null
        }
    }

    asData(): { [k: string]: any } {
        return locationSchema.parse({
            title: this.title,
            hoursPreceding: this.hoursPreceding,
            timezone: this.timezone,
            reservationType: this.reservationType,
            hoursPrecedingPattern: this.hoursPrecedingPattern,
            location: this.lat !== undefined && this.lng !== undefined ? new GeoPoint(this.lat, this.lng) : undefined
        })
    }

    assignmentDateForDate = (date: Date): Date => {
        const forWeekday = moment.tz(date, this.timezone).isoWeekday()
        const hoursPreceding = this.hoursPrecedingPattern[forWeekday - 1] ?? this.hoursPreceding

        const extraDaysPreceding = Math.floor(hoursPreceding / 24)

        return moment
            .tz(date, this.timezone)
            .startOf("day")
            .subtract(hoursPreceding % 24, "hours")
            .subtract(extraDaysPreceding, "days")
            .toDate()
    }

    assignmentDoneForDate = (date: Date): boolean => {
        return moment().isSameOrAfter(this.assignmentDateForDate(date))
    }

    toString(): string {
        return `${this.title} (${this.id})`
    }
}
